import * as React from "react";
import { Icon, Color, SmallIcon } from "@utdanningsdirektoratet/icon";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { isEmpty as isEmptyString } from "@utdanningsdirektoratet/utils/string";
import styles from "./Select.module.less";

interface SelectProps extends React.InputHTMLAttributes<HTMLSelectElement> {
  options?: Array<{
    text: string;
    value?: string | ReadonlyArray<string> | number;
    disabled?: boolean;
    hidden?: boolean;
    selected?: boolean;
  }>;
  id?: string;
  label?: React.ReactNode | string;
  disabled?: boolean;
  required?: boolean;
  message?: React.ReactNode | string;
  info?: boolean;
  error?: boolean;
  className?: string;
  arrowClassName?: string;
  icon?: SmallIcon | "none";
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  erroralways?: boolean;
  children?: React.ReactNode;
}

const Select: React.FC<SelectProps> = (props) => {
  const id = useId(props.id);
  const [isFocused, setIsFocused] = React.useState(false);

  const {
    options = [],
    value,
    disabled,
    required,
    message,
    label,
    error,
    info,
    icon = info && !error ? "circle info" : "warning",
    className,
    onChange,
    onFocus,
    onBlur,
    arrowClassName,
    erroralways,
    children,
    style,
    ...rest
  } = props;

  const onFocusWrapped = (event: React.FocusEvent<HTMLSelectElement>): void => {
    if (onFocus) {
      onFocus(event);
    }
    setIsFocused(true);
  };

  const onBlurWrapped = (event: React.FocusEvent<HTMLSelectElement>): void => {
    if (onBlur) {
      onBlur(event);
    }
    setIsFocused(false);
  };

  const isEmpty =
    (!value && value !== 0 && options.some((o) => o.hidden)) ||
    value === "null" ||
    value === "undefined" ||
    isEmptyString(value);

  const inputClass = cls({
    [styles.select]: true,
    [styles.selectEmpty]: isEmpty,
    [styles.selectError]: error,
    [styles.selectInfo]: info,
    [styles.selectDisabled]: disabled,
    [styles.selectWithPadding]: icon && (error || info),
  });

  const iconClass = cls({
    [styles.selectIcon]: true,
    [styles.selectIconVisible]: error || info,
  });

  const labelClass = cls(
    {
      [styles.selectLabel]: true,
      [styles.selectLabelFocused]: isFocused,
    },
    className,
  );

  const wrapperClass = cls({
    [styles.selectWrapper]: true,
    [styles.selectWrapperEmpty]: isEmpty,
  });

  const arrowClass = cls(
    {
      [styles.selectArrow]: true,
    },
    cls(arrowClassName),
  );

  let iconColor;
  if (error) iconColor = "darkred" as Color;
  if (info) iconColor = "darkblue" as Color;

  return (
    <label htmlFor={id} id={`${id}-label`} className={labelClass} style={style}>
      {label}
      <Tooltip
        message={message}
        disabled={!(error || info)}
        alwaysShow={erroralways === undefined ? error : erroralways}
        align="right"
        tabIndex={-1}
        alert
      >
        <span className={wrapperClass}>
          <select
            {...rest}
            required={required}
            aria-required={required}
            disabled={disabled}
            aria-invalid={error}
            aria-label={rest["aria-label"]}
            aria-labelledby={label ? `${id}-label` : undefined}
            className={inputClass}
            id={id}
            value={!isEmpty ? value : ""}
            onChange={onChange}
            onFocus={onFocusWrapped}
            onBlur={onBlurWrapped}
          >
            {options.map((option, i) => (
              <option
                key={`${id}-option-${i}`}
                value={option.value}
                disabled={option.disabled}
                hidden={option.hidden}
              >
                {option.text}
              </option>
            ))}
            {children}
          </select>
          {!(error || info) && (
            <Icon icon="angle down" className={arrowClass} />
          )}
          {icon ? (
            <Icon
              className={iconClass}
              color={iconColor}
              aria-hidden={error || info ? "false" : "true"}
              icon={icon}
            />
          ) : null}
        </span>
      </Tooltip>
    </label>
  );
};

export default Select;
